import { equals, find } from '@execonline-inc/collections';
import { emptyFragment } from '@execonline-inc/execonline-ui';
import { fromEmpty, fromNullable, Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { isNumber } from '../../../../../../../../Conditions';
import {
  ExperienceResource,
  ExperienceResourceArray,
} from '../../../../../CurrentCollectionStep/BuilderV2/Types';
import { previewStore } from '../../../../LearningCollectionPreviewStore';
import {
  ConfirmingCancel,
  ConfirmingCancelFromCreate,
  ConfirmingCancelFromPublish,
  ConfirmingCancelFromSaveAndEdit,
  Creating,
  DiscardingChanges,
  Loading,
  Publishing,
  Ready,
  RecommendationsIncoming,
  SaveAndEditing,
  SavingFromCreating,
  SavingFromPublishing,
  SavingFromSaveAndEditing,
  Waiting,
} from '../../../../LearningCollectionPreviewStore/Types';
import ExperienceCard from '../DefaultSection/ExperienceCard';
import ExperienceSection from '../DefaultSection/ExperienceSection';
import RefreshingSpinner from '../DefaultSection/RefreshingSpinner';

export interface Props {
  experiencesArray: ExperienceResourceArray;
}

function isLoading(
  store: typeof previewStore,
): store is typeof previewStore & { state: Loading | Waiting | DiscardingChanges } {
  return (
    store.state.kind === 'loading' ||
    store.state.kind === 'waiting' ||
    store.state.kind === 'discarding-changes'
  );
}

function hasResource(store: typeof previewStore): store is typeof previewStore & {
  state:
    | Ready
    | RecommendationsIncoming
    | Publishing
    | Creating
    | SaveAndEditing
    | ConfirmingCancel
    | ConfirmingCancelFromPublish
    | ConfirmingCancelFromCreate
    | ConfirmingCancelFromSaveAndEdit
    | SavingFromPublishing
    | SavingFromCreating
    | SavingFromSaveAndEditing;
} {
  return (
    store.state.kind === 'ready' ||
    store.state.kind === 'recommendations-incoming' ||
    store.state.kind === 'publishing' ||
    store.state.kind === 'creating' ||
    store.state.kind === 'save-and-editing' ||
    store.state.kind === 'confirming-cancel' ||
    store.state.kind === 'confirming-cancel-from-publish' ||
    store.state.kind === 'confirming-cancel-from-create' ||
    store.state.kind === 'confirming-cancel-from-save-and-edit' ||
    store.state.kind === 'saving-from-publishing' ||
    store.state.kind === 'saving-from-creating' ||
    store.state.kind === 'saving-from-save-and-editing'
  );
}

function RecommendationsIncomingSpinner() {
  const element = document.getElementById('preview-container');
  if (previewStore.state.kind !== 'recommendations-incoming') {
    return emptyFragment();
  }
  {
    return fromNullable(element)
      .map<ReactNode>((element) =>
        createPortal(
          <div className="absolute inset-0 bg-white/50 z-50 flex items-center justify-center">
            <RefreshingSpinner />
          </div>,
          element,
        ),
      )
      .getOrElse(emptyFragment);
  }
}

const findExperienceResource = (
  ex: number,
  experiencesArray: ExperienceResource[],
): Maybe<ExperienceResource> =>
  find((resource: ExperienceResource) => equals(resource.payload.id, ex), experiencesArray);

function RecommendedSection({ experiencesArray }: Props) {
  if (isLoading(previewStore)) {
    return <RefreshingSpinner />;
  }

  if (hasResource(previewStore)) {
    const { name, description, experiences } =
      previewStore.state.resource.payload.recommended_product_collection;
    const validatedExperiences = fromEmpty(experiences.filter(isNumber));

    return (
      <>
        {validatedExperiences
          .map((exs) => (
            <div className="flex flex-grow flex-col gap-y-8">
              <div className="bg-white shadow-section">
                <ExperienceSection name={name} description={description}>
                  <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
                    <>
                      {exs.map((ex) => {
                        const experienceResource = findExperienceResource(ex, experiencesArray);
                        return <ExperienceCard experienceResource={experienceResource} key={ex} />;
                      })}
                    </>
                  </div>
                </ExperienceSection>
              </div>
            </div>
          ))
          .getOrElse(emptyFragment)}
        <RecommendationsIncomingSpinner />
      </>
    );
  }

  return emptyFragment();
}
export default observer(RecommendedSection);
